import { useState } from "react";
import { ArrowsOut, DownloadSimple, Info, MagnifyingGlassMinus, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PLANS = [
  {
    id: "cask",
    name: "Cask Room",
    capacityLabel: "Up to 80 guests",
    dimensions: "12.5m × 15.15m",
    description:
      "Intimate stone-walled room with original fireplace, exposed timber beams and a long bridal table along the far wall.",
    image: "/assets/gledswood/floor-plans/cask-room.png",
  },
  {
    id: "country",
    name: "Country Room",
    capacityLabel: "Up to 130 guests",
    dimensions: "19.1m × 15.24m",
    description:
      "Spacious open room with stage, bar, dance floor and bridal table along the long wall.",
    image: "/assets/gledswood/floor-plans/country-room.png",
  },
  {
    id: "gledswood",
    name: "Gledswood Room",
    capacityLabel: "",
    dimensions: "14.4m × 40.7m",
    description:
      "Our grandest indoor space with a fixed stage, dance floor, bar and verandah — perfect for larger celebrations.",
    image: "/assets/gledswood/floor-plans/gledswood-room.png",
  },
  {
    id: "shearing",
    name: "Shearing Shed",
    capacityLabel: "",
    dimensions: "10.9m × 18.1m",
    description:
      "Rustic timber shearing shed with long banquet tables — perfect for country-style celebrations.",
    image: "/assets/gledswood/floor-plans/shearing-shed.png",
  },
];

export default function FloorPlans() {
  const [zoomed, setZoomed] = useState(null);
  const [magnified, setMagnified] = useState(false);

  const openZoom = (plan) => {
    setMagnified(false);
    setZoomed(plan);
  };

  return (
    <div data-testid="floor-plans-page">
      <PageHeader
        eyebrow="Reception"
        title="Floor Plans"
        description="Venue-managed room layouts, drawn to scale for your planning."
      />
      <p className="max-w-3xl text-sm text-muted-foreground" data-testid="floor-plans-lead">
        Below are the floor plans for each of our function rooms. The floor plan is drawn to scale
        and shows the maximum amount of furniture that fits into the room. The floor plan can be
        modified to suit your guest numbers. Tap any plan to enlarge it.
      </p>
      <section
        className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2"
        data-testid="floor-plan-cards"
      >
        {PLANS.map((plan) => (
          <PlanCard key={plan.id} plan={plan} onZoom={openZoom} />
        ))}
      </section>
      <aside
        className="mt-8 flex items-start gap-3 border border-border bg-muted/40 p-5 rounded-lg"
        data-testid="floor-plans-seating-note"
      >
        <Info size={20} className="mt-0.5 shrink-0 text-secondary" />
        <p className="text-sm text-muted-foreground">
          Guests are seated 1–10 in a clockwise direction. The person in seat 1 faces the bridal
          table. Any blank/unused tables will be removed from the final layout.
        </p>
      </aside>

      <Dialog open={!!zoomed} onOpenChange={(open) => !open && setZoomed(null)}>
        <DialogContent className="max-w-[min(96vw,1100px)] p-4 sm:p-6" data-testid="floorplan-zoom-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl sm:text-3xl text-primary pr-8">
              {zoomed?.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {zoomed?.description}
            </DialogDescription>
          </DialogHeader>
          {zoomed && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => setMagnified(!magnified)}
                  data-testid="floorplan-zoom-toggle"
                >
                  {magnified ? (
                    <MagnifyingGlassMinus size={16} className="mr-1" />
                  ) : (
                    <MagnifyingGlassPlus size={16} className="mr-1" />
                  )}
                  {magnified ? "Fit to screen" : "Zoom in"}
                </Button>
                <Button asChild variant="outline" size="sm" className="rounded-full">
                  <a
                    href={zoomed.image}
                    download={`gledswood-${zoomed.id}-floor-plan.png`}
                    data-testid="floorplan-zoom-download"
                  >
                    <DownloadSimple size={16} className="mr-1" />
                    Download plan
                  </a>
                </Button>
                <span className="text-xs text-muted-foreground">
                  {zoomed.dimensions}
                  {zoomed.capacityLabel ? ` · ${zoomed.capacityLabel}` : ""}
                </span>
              </div>
              <div className="mt-3 max-h-[70vh] overflow-auto bg-white border border-border rounded">
                <img
                  src={zoomed.image}
                  alt={`${zoomed.name} floor plan`}
                  onClick={() => setMagnified(!magnified)}
                  className={[
                    "mx-auto object-contain",
                    magnified ? "w-[220%] max-w-none cursor-zoom-out" : "w-full cursor-zoom-in",
                  ].join(" ")}
                  data-testid="floorplan-zoom-image"
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlanCard({ plan, onZoom }) {
  return (
    <article
      className="border border-border bg-card p-5 rounded-lg"
      data-testid={`floorplan-card-${plan.id}`}
    >
      <h2 className="font-heading text-2xl md:text-3xl text-primary">{plan.name}</h2>
      {plan.capacityLabel && (
        <p className="mt-1 text-sm text-secondary" data-testid={`floorplan-capacity-${plan.id}`}>
          {plan.capacityLabel}
        </p>
      )}
      <p className="mt-1 text-sm text-muted-foreground" data-testid={`floorplan-dimensions-${plan.id}`}>
        {plan.dimensions}
      </p>
      <button
        type="button"
        onClick={() => onZoom(plan)}
        aria-label={`Enlarge ${plan.name} floor plan`}
        className="group relative mt-4 block w-full overflow-hidden border border-border bg-white rounded transition-colors duration-200 hover:border-secondary"
        data-testid={`floorplan-image-${plan.id}`}
      >
        <img
          src={plan.image}
          alt={`${plan.name} floor plan`}
          loading="lazy"
          className="aspect-[3/4] w-full object-contain p-2"
        />
        <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-primary/85 px-2 py-1 text-[11px] uppercase tracking-wide text-primary-foreground rounded-full">
          <ArrowsOut size={11} />
          Tap to enlarge
        </span>
      </button>
      <p className="mt-4 text-sm text-muted-foreground">{plan.description}</p>
    </article>
  );
}
